import { fateSentence, type BroadcastDecision } from '../shared/derivation.js';
import type { FeedView, OnAir } from '../shared/types.js';
import { airPath } from '../shared/air-path.js';

/**
 * The client's decisions, with no DOM in them.
 *
 * Issue 010. Two code reviews of wave 3 found seven defects in `src/client/` and
 * **not one was found by a test**, because nothing executes that code. The
 * checks that did cover it were source scans, which assert that a line exists,
 * cannot see behaviour, and were themselves wrong twice on their first run.
 *
 * The fix is not a headless DOM. Six of the seven were pure logic wearing a DOM
 * costume: a guard in the wrong order, a backoff that was never consulted, a
 * flag nobody reset, a button derived from the wrong predicate. Only ONE needed
 * a browser (that tile elements survive a state update), and that one already
 * has a browser check with a liveness control.
 *
 * So the logic moves here, where a test can reach it with no dependency at all,
 * and the renderers become what they should have been: functions that take a
 * decision and write it into elements. This is wave 3's own working rule 2, and
 * the same move that turned `fateContext` and `fateSentence` into gates that
 * each caught a real defect the day they were written.
 */

// --- the director's tiles ---------------------------------------------------

export interface TileView {
  className: string;
  /** WHICH ANGLE this stream is. Never the director's good/bad label. */
  angle: string;
  operator: string;
  score: string;
  bits: string;
  /** EVERY blocker, joined. Showing the first is how an operator fixes one thing and sees nothing change. */
  blockers: string;
  gates: string;
  fate: string;
  takeText: string;
  /** True when the SERVER would refuse the take. Never derived from the mechanical gate alone. */
  takeDisabled: boolean;
  takeTitle: string;
}

/** Which gates hold, and whether a local model or the judge said so. */
export function gateLabel(feed: FeedView): string {
  const one = (name: string, v: FeedView['visual']): string => {
    if (v === null) return `${name}:waiting`;
    if (v.error !== undefined) return `${name}:error`;
    return `${name}:${v.approved ? 'ok' : 'no'}/${v.decidedBy === 'local' ? 'local' : 'judge'}`;
  };
  return `${one('pic', feed.visual)}  ${one('spk', feed.speech)}`;
}

export function tileView(
  feed: FeedView,
  live: boolean,
  decision: BroadcastDecision | null,
): TileView {
  const v = feed.vitals;
  return {
    className: `tile${live ? ' live' : ''}${feed.eligible ? '' : ' failing'}`,
    angle: feed.angle,
    operator: feed.operator,
    score: feed.score ? `${(feed.score.overall * 100).toFixed(0)}%` : '--',
    bits: v ? `${Math.round(v.bitrate / 1000)}k ${v.fps.toFixed(0)}fps ${v.frameHeight}p` : 'no vitals',
    blockers: feed.blockers.join(', '),
    gates: gateLabel(feed),
    fate: fateSentence(feed.id, decision),
    takeText: live ? 'ON AIR' : (feed.soundEligible ? 'Take' : 'Take (silent)'),
    // The server's ruling, never `score.passes`. A control is an advertised
    // action, and this one offered what the server would refuse for a whole wave.
    takeDisabled: live || !feed.eligible,
    /*
     * A takeable feed that will go out SILENT says so on the button it is
     * offered from, because "Take" and "Take (silent)" are different acts and
     * the split made the second one reachable. Without it a director takes a
     * clean-looking picture and the sound simply is not there, with the reason
     * available nowhere on the control that did it.
     */
    takeTitle: feed.eligible
      ? (feed.soundEligible ? '' : `goes to air silent: ${feed.soundBlockers.join(', ')}`)
      : feed.blockers.join(', '),
  };
}

// --- the director's monitor connections -------------------------------------

/** What `syncMonitors` should do this tick. No connections are touched here. */
export interface MonitorPlan {
  /** Feeds to start negotiating. */
  connect: string[];
  /** Connections to close and forget: the feed is gone. */
  close: string[];
  /** Backoff entries to drop, for the same reason. */
  forget: string[];
}

export function monitorPlan(
  feeds: readonly { id: string }[],
  state: {
    connected: ReadonlySet<string>;
    connecting: ReadonlySet<string>;
    failedUntil: ReadonlyMap<string, number>;
    rendered: ReadonlySet<string>;
  },
  now: number,
): MonitorPlan {
  const live = new Set(feeds.map((f) => f.id));
  return {
    connect: feeds
      .map((f) => f.id)
      .filter((id) => !state.connected.has(id))
      .filter((id) => !state.connecting.has(id))
      // The backoff is the READ, not the write. Without consulting it a feed
      // whose negotiation always fails gets a fresh peer connection on every
      // state message: at 2Hz, per feed, against the media server, forever.
      .filter((id) => now >= (state.failedUntil.get(id) ?? 0))
      .filter((id) => state.rendered.has(id)),
    close: [...state.connected].filter((id) => !live.has(id)),
    forget: [...state.failedUntil.keys()].filter((id) => !live.has(id)),
  };
}

// --- the viewer's stage -----------------------------------------------------

export type Stage =
  | { kind: 'slate'; reason: string }
  | { kind: 'feed'; path: string; credit: string; changed: boolean }
  | { kind: 'wait' };

/**
 * What the viewer should be showing.
 *
 * `holdSeconds` is checked BEFORE anything is decided, and the order is the bug
 * it exists to prevent: the renderer used to record which feed it was showing
 * and THEN return if the hold was unknown, which poisons the state so every
 * later call short-circuits and no player is ever created.
 */
export function viewerStage(
  feeds: readonly FeedView[],
  onAir: OnAir,
  currentPath: string | null,
  holdSeconds: number | null,
): Stage {
  if (holdSeconds === null) return { kind: 'wait' };
  if (onAir.kind === 'slate') return { kind: 'slate', reason: onAir.reason };
  const feed = feeds.find((f) => f.id === onAir.feedId);
  if (feed === undefined) return { kind: 'slate', reason: 'feed dropped' };
  /*
   * Stage 2: a silent broadcast is served from a DIFFERENT path, one fed by a
   * copy remux that drops the audio track. Muting the player would deliver the
   * refused audio to every viewer and leave the unmute button one click away,
   * which is a refusal the person it protects somebody from can override.
   *
   * `changed` compares against the path actually being played, so a feed that
   * goes silent while live is a path change and the player is rebuilt. That is
   * the point: it has to stop pulling the segments that carry the sound.
   */
  const soundFeed = onAir.sound === null ? null : feeds.find((f) => f.id === onAir.sound);
  // A sound source the viewer does not know about is treated as silence rather
  // than as this feed's own audio. Falling back to the picture's path would
  // deliver audio the switcher did not choose, which is the wrong direction.
  const path = airPath(feed.path, onAir.sound === null ? null : soundFeed?.path ?? null);
  return {
    kind: 'feed',
    path,
    credit: `${feed.angle} · ${feed.operator}`,
    changed: currentPath !== path,
  };
}

// --- the viewer's hold, enforced at the seek ---------------------------------

/**
 * Where a seek is allowed to land.
 *
 * The native video controls are a control, and a control is an advertised
 * action: the scrubber advertises "watch live", which is exactly the action the
 * hold exists to refuse. The watchdog in `delayed.ts` was written against
 * hls.js drifting to the live edge on its own; a person dragging there is the
 * same threat through the human door, and a 2s correction interval is 2s of
 * unruled material. So the clamp runs AT THE SEEK. Seeking backward is left
 * alone: everything behind the hold has already been ruled on.
 */
export function holdClamp(desired: number, edge: number, start: number, holdSeconds: number): number {
  return Math.max(start, Math.min(desired, edge - holdSeconds));
}

// --- the publisher's failed start --------------------------------------------

export interface StartFailure {
  status: string;
  buttonText: string;
  buttonDisabled: boolean;
}

/**
 * What the page shows when going live fails AFTER the camera opened.
 *
 * Only `getUserMedia` was guarded. WHIP negotiation throws on a refused fetch
 * and again on any non-2xx from the media server, and neither was caught, so a
 * failure there left the button disabled reading "Starting...", the camera
 * running with its tally light on, and no route back except reloading the page.
 * On a phone, at an event, that is the whole session.
 *
 * The button being re-enabled is the load-bearing part: a publisher whose first
 * attempt failed for a transient reason (media server still booting, wifi
 * dropping as they walk in) should be able to press it again.
 */
export function startFailure(err: unknown): StartFailure {
  return {
    status:
      `Could not go live: ${String(err)}. The camera has been released, ` +
      'so nothing is being captured. Fix the problem and press Go live again.',
    buttonText: 'Go live',
    buttonDisabled: false,
  };
}

// --- the publisher's own verdict --------------------------------------------

export interface PublisherView {
  className: string;
  text: string;
}

/**
 * What a publisher is told about themselves.
 *
 * `wasOnAir` distinguishes "never got on" from "was on and got pulled", which is
 * the moment an operator most deserves a straight answer. It is also why a
 * VOLUNTARY stop must reset it: a publisher who stopped on purpose and went live
 * again was being told the switchboard had pulled them.
 */
export function publisherView(
  live: boolean,
  blockers: readonly string[],
  wasOnAir: boolean,
  registered: boolean,
  connected = true,
): PublisherView {
  /**
   * A ruling is a statement about what the SERVER holds, and a page that cannot
   * reach the server does not have one.
   *
   * This read "Eligible" in green over a control server holding zero feeds,
   * because the socket had dropped and the page went on rendering the last
   * frame it received. The publisher was told they were one click from air; the
   * director's wall was empty. Nothing on either surface said the two had
   * stopped talking.
   *
   * Absence of a verdict is never approval. That rule was written for the
   * server's gates and it governs the page that reports them too: an unreachable
   * switchboard is an instrument failure, and an instrument failure is never a
   * judgment. ON AIR is the most dangerous thing to leave on screen, so it goes
   * first.
   */
  /*
    * Plain words, because this is the PUBLIC page. The director's own surface
    * keeps "take" and "ON AIR": that is a professional tool and the vocabulary
    * is correct there. Here the reader is somebody who has streamed to their
    * friends and never broadcast anything, and "Eligible, waiting for the
    * director" asks them to translate twice before they learn they are fine.
    */
  if (!connected) {
    return {
      className: 'verdict unknown',
      text: wasOnAir
        ? 'Lost connection. You may no longer be live.'
        : 'Lost connection. This page cannot tell you whether you are live.',
    };
  }
  if (live) return { className: 'verdict pass', text: "YOU'RE LIVE" };
  if (blockers.length === 0) {
    return {
      className: 'verdict pass',
      text: registered ? 'Ready. Waiting for the newsroom to pick you.' : 'Ready',
    };
  }
  return {
    className: 'verdict fail',
    text: wasOnAir ? 'You were taken off' : 'Not live yet',
  };
}


// --- what the SCREEN says, which is not what the control plane says ----------

/**
 * Whether the media plane is actually delivering, as the page can observe it.
 *
 * `hasFrames` comes from the element having produced a real frame, not from
 * `play()` resolving. `play()` resolving means playback was permitted; it says
 * nothing about whether any bytes arrived.
 */
export interface MediaHealth {
  /** The element has decoded at least one frame of the CURRENT path. */
  hasFrames: boolean;
  /** When the player was attached to the current path. */
  attachedAt: number;
  /** hls.js reported a fatal error, or the element did. */
  fatal: boolean;
}

/** What the body should say it is doing. Four states, not three. */
export type Screen = 'wait' | 'connecting' | 'live' | 'dark' | 'slate';

/**
 * `live` must mean a frame arrived. Wave 5, defect D1 and call 4.
 *
 * Measured 2026-08-29: with HLS failing outright, the element read
 * `readyState 0`, `videoWidth 0`, `seekable.length 0`, and the page read
 * `data-state="live"` with a credit line and "Held 32s behind live" over a
 * black rectangle. The control plane and the picture could disagree for an
 * unbounded time and the page always believed the control plane.
 *
 * `dark` is deliberately NOT the slate. The slate is an editorial decision that
 * nothing met the standard; a failed manifest fetch is a plumbing failure on one
 * viewer's device. Rendering the second as the first is the same lie in a better
 * costume: it would tell a viewer the newsroom refused everything when in fact
 * this phone cannot reach the media plane.
 *
 * The grace window exists so a slow first segment does not flash an error. A
 * FATAL error skips it, because waiting out a grace period on an error that has
 * already been reported is just a slower lie.
 */
export function viewerScreen(
  stage: Stage,
  media: MediaHealth | null,
  now: number,
  graceMs: number,
): Screen {
  if (stage.kind === 'wait') return 'wait';
  if (stage.kind === 'slate') return 'slate';
  // A feed is on air but nothing has been attached yet.
  if (media === null) return 'connecting';
  if (media.hasFrames) return 'live';
  if (media.fatal) return 'dark';
  return now - media.attachedAt > graceMs ? 'dark' : 'connecting';
}

/**
 * The hold is named only when something is actually being held. D6.
 *
 * `paintDelay` ran on a 2s interval regardless of state and fell back to the
 * static string whenever there was no player, so the slate carried "Held 32s
 * behind live" while nothing was playing and nothing was held. A true sentence
 * about a state the page is not in is still a false page.
 */
export function holdLabel(screen: Screen, holdSeconds: number | null, behind: number | null): string {
  if (screen !== 'live' || holdSeconds === null) return '';
  return behind === null
    ? `Held ${holdSeconds}s behind live`
    : `Held ${behind.toFixed(0)}s behind live so the checks land first`;
}

// --- the account links in the header ------------------------------------------

/**
 * What one row of the node index says, and what the page says when there are
 * none.
 *
 * A DECISION rather than markup, for the reason `navLinks` gives above: nothing
 * executes the client, so every client defect this repository has found lived in
 * logic exactly like this, and the fix was never a headless DOM.
 *
 * The empty cases are the point. **A node with no channels and a filter that
 * matched nothing are different facts**, and rendering both as an empty list is
 * the null-is-not-zero defect this repository counts nine of, arriving in a
 * page. A reader who typed a filter needs to know their filter is why.
 */
export interface AirRowView {
  id: string;
  title: string;
  live: boolean;
  /** What to show under the title. Never empty, never an error string. */
  detail: string;
  /** The clause a viewer may be told, when one fired and may be named. */
  clause?: string;
  /** Where the row goes. A channel, never a feed. */
  href: string;
}

export function airRow(row: {
  id: string; title: string; live: boolean;
  slate?: { mark: string; why: string; clause?: string; withheld?: number };
}): AirRowView {
  const view: AirRowView = {
    id: row.id,
    title: row.title,
    live: row.live,
    detail: row.live
      ? 'Broadcasting now.'
      : row.slate?.why ?? 'Not broadcasting.',
    href: `/channels.html#${row.id}`,
  };
  if (row.live) return view;
  /*
   * Reason, then the clause, then the count. That ORDER is the viewer's, in
   * `broadcast.ts`, and matching it is not tidiness: built the other way round
   * the clause landed orphaned after the withheld sentence, reading "...2 more
   * rules fired that cannot be named here. visual.not-the-event". Found by
   * rendering the page with a live payload, which is the only place a sentence
   * ordering is visible at all.
   *
   * A withheld COUNT and never a name, which is the contract the viewer's slate
   * already has: eight of the fifteen rejecting clauses may never be named
   * publicly, because naming one IS the disclosure it prevents.
   */
  const withheld = row.slate?.withheld ?? 0;
  const parts = [view.detail];
  if (row.slate?.clause !== undefined) parts.push(`Held under ${row.slate.clause}.`);
  if (withheld > 0) {
    parts.push(`${String(withheld)} more rule${withheld === 1 ? '' : 's'} fired that cannot be named here.`);
  }
  return {
    ...view,
    detail: parts.join(' '),
    ...(row.slate?.clause !== undefined ? { clause: row.slate.clause } : {}),
  };
}

/** What the page says instead of a list, or null when there is a list. */
export function airEmptyCopy(total: number, shown: number, query: string): string | null {
  if (shown > 0) return null;
  if (total === 0) {
    return 'This node carries no channels. That is a fact about how it is '
      + 'configured, not about whether anybody is streaming.';
  }
  return `No channel here matches ${JSON.stringify(query)}. `
    + `This node carries ${String(total)}.`;
}

export interface NavState {
  signedIn: boolean;
  /** Null while the server has not answered yet, or for an account with none. */
  handle: string | null;
}

export interface NavLink {
  kind: 'link' | 'signout' | 'text';
  label: string;
  href: string;
  /** Set where a link is hidden at phone width. See `.nav-handle` in styles. */
  className?: string;
}

/**
 * What the header offers, given who is signed in.
 *
 * A decision rather than markup, so a test can reach it: every client defect
 * this repository has found lived in logic exactly like this, and the fix was
 * never a headless DOM but moving the decision here.
 *
 * **Signed out never hides the product.** The publish link is offered to
 * everyone, because anyone in the building may offer a feed and an account buys
 * a handle rather than the right to broadcast. A nav that put "sign up" where
 * "go live" should be would misdescribe the whole thing.
 */
export function navLinks(state: NavState): NavLink[] {
  // ONE link, because wave 5 made watching and publishing one page. Offering
  // "watch" and "go live" separately would describe a split that no longer
  // exists and send both to the same URL.
  // "watch" rather than "broadcast": the label names what the reader will do,
  // in the word they already use for it. Reported 2026-08-29, and the point
  // generalises past the one word. See `shared/slate-copy.ts`.
  /*
   * `channels` is in the COMMON set, so a signed-out reader sees it too. The
   * rules a channel publishes are readable by anybody, and hiding the link
   * behind an account would make the one thing this project asks to be judged
   * on visible only to people who already joined.
   */
  const common: NavLink[] = [
    { kind: 'link', label: 'watch', href: '/' },
    /*
     * `on air` is COMMON too, and it is the page that answers "is anything
     * happening here" without knowing a channel id. A node index behind an
     * account would be a directory only members can read, which is the shape
     * this plan's audit found missing rather than a shape to rebuild.
     */
    { kind: 'link', label: 'on air', href: '/on-air.html' },
    { kind: 'link', label: 'channels', href: '/channels.html' },
  ];
  if (!state.signedIn) {
    return [...common, { kind: 'link', label: 'sign in', href: '/account.html' }];
  }
  return [
    ...common,
    // Null handle means signed in and the server has not said who yet, or an
    // account whose profile could not be read. Linking to /u/null would 404, so
    // the profile link only appears once there is a handle to point at.
    // The handle is identity rather than navigation, so it is the first thing
    // dropped at phone width: `.nav-handle` is hidden under 620px, where it
    // otherwise wrapped onto three lines and pushed `sign out` off the screen.
    ...(state.handle === null
      ? [{ kind: 'text' as const, label: 'signed in', href: '' }]
      : [{ kind: 'link' as const, label: `@${state.handle}`, href: `/u/${state.handle}`, className: 'nav-handle' }]),
    { kind: 'link', label: 'settings', href: '/account.html' },
    { kind: 'signout', label: 'sign out', href: '#' },
  ];
}

/**
 * What to say after saving settings.
 *
 * A decision because the statuses mean different things and a person has to act
 * differently on each: 409 is somebody else's handle and needs a new one, 503 is
 * the account store being down and needs waiting, 401 is a session that expired
 * mid-form. Rendering "error" for all three would leave a person guessing which.
 */
export function saveMessage(status: number, body: { error?: string; fix?: string }): string {
  if (status >= 200 && status < 300) return 'Saved.';
  if (status === 409) return 'That handle is taken. Pick another one.';
  if (status === 401) return 'Your session expired. Sign in again.';
  if (status === 503) {
    return 'The account store is unavailable, so nothing was saved. '
      + 'This does not affect publishing: you can still go live.';
  }
  const detail = [body.error, body.fix].filter(Boolean).join(' ');
  return detail || `That did not save (${status}).`;
}


// --- signing in, or creating an account ---------------------------------------

export type AuthMode = 'signin' | 'signup';

export interface AuthCopy {
  title: string;
  /** The one primary button. There is never a second thing to weigh. */
  primary: string;
  /** Shown under the password only where it is a rule rather than a guess. */
  passwordHint: string;
  switchPrompt: string;
  switchLabel: string;
  passwordAutocomplete: 'current-password' | 'new-password';
}

/**
 * One mode at a time, with the other offered as a link.
 *
 * The first version put "Sign in" and "Create an account" side by side as equal
 * buttons over one form, which asks a person to classify themselves before
 * they can act and gives no clue what happens if they choose wrong. It also
 * showed "at least 6 characters" to someone signing in, where it is not a rule
 * but a hint about a password they already have.
 *
 * A mode with one primary action and a link to the other is the ordinary
 * arrangement because it answers both: what the button will do, and how to get
 * to the other thing. `autocomplete` follows the mode too, or a password
 * manager offers to save a login as a new credential.
 */
export function authCopy(mode: AuthMode): AuthCopy {
  if (mode === 'signup') {
    return {
      title: 'Create an account',
      primary: 'Create account',
      passwordHint: 'At least 6 characters.',
      switchPrompt: 'Already have an account?',
      switchLabel: 'Sign in',
      passwordAutocomplete: 'new-password',
    };
  }
  return {
    title: 'Sign in',
    primary: 'Sign in',
    passwordHint: '',
    switchPrompt: 'New to switchboard?',
    switchLabel: 'Create an account',
    passwordAutocomplete: 'current-password',
  };
}

// --- a sheet that behaves like a dialog ---------------------------------------

/**
 * Should this keypress close an open dialog?
 *
 * Escape, and only Escape. A decision rather than an inline comparison so a
 * test can reach it: the sheet looked like a modal and behaved like a div, and
 * "does Escape close it" was answerable only by opening a browser.
 */
export function closesDialog(key: string, isOpen: boolean): boolean {
  return isOpen && key === 'Escape';
}

/**
 * Where Tab should go, to keep focus inside an open dialog.
 *
 * Returns the index to move to, or null to let the browser handle it. Focus
 * escaping a modal is the defect that makes a dialog a decoration: the sheet
 * covers the page, and Tab walked straight out of it into controls the person
 * could not see.
 *
 * Wraps in both directions, because Shift+Tab off the first element is the same
 * bug as Tab off the last one and is the half people forget.
 */
export function trapIndex(current: number, count: number, shift: boolean): number | null {
  if (count === 0) return null;
  if (shift && current <= 0) return count - 1;
  if (!shift && current >= count - 1) return 0;
  return null;
}

/**
 * The media plane's shared secret, taken from the page's own URL.
 *
 * With `MEDIA_AUTH=on` the media plane refuses a WHIP publish without the venue
 * token and refuses a WHEP read of anything not currently on air without the
 * operator's. Both arrive the same way: an operator bookmarks
 * `.../director.html?token=...` and shares the publish link the same way.
 *
 * The query string rather than a field on the page, for one blunt reason: the
 * publisher surface is used by somebody standing up holding a phone, and a form
 * to fill in before a camera opens is a form that does not get filled in. A
 * viewer needs no token at all, because what is on air is public and that is the
 * whole rule.
 *
 * **This is ACCESS and not IDENTITY.** A shared secret says what a request may
 * reach; it says nothing about who sent it, whether a rating can be trusted, or
 * what a durable account means. Those are `server/identity.ts` and the proposal
 * behind it, and the two are deliberately not wired together: one guards a media
 * path, the other guards a person.
 */
export function mediaToken(search: string): string {
  try {
    return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('token') ?? '';
  } catch {
    return '';
  }
}

/**
 * Appends the token, preserving any query the URL already carries.
 *
 * An empty token returns the URL untouched rather than appending `token=`,
 * because an empty credential presented is not the same request as no
 * credential presented, and on a LAN with the door open the second is correct.
 */
export function withToken(url: string, token: string): string {
  if (token === '') return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}


/**
 * The link from a surface that refused you to the rule it refused you by.
 *
 * Plan section 3, items 3 and 4. The slate is where a viewer is most likely to
 * want the rule and the publisher page is where the person who can fix the shot
 * is standing, so both carry it. It is one function because it is one decision:
 * a link that is present exactly when there is somewhere real to point.
 *
 * Returns null for an empty URL rather than a dead anchor. A link that goes
 * nowhere is worse than no link, because it builds trust before it fails, and
 * this repository has that written down twice already.
 */
export function standardLink(standardUrl: string): { href: string; label: string } | null {
  const href = standardUrl.trim();
  if (href === '') return null;
  return { href, label: 'Read the standard' };
}

/**
 * What a publisher is told about disagreeing, or nothing at all.
 *
 * Plan section 5: the route does not wait on attribution, and it is what makes
 * a ruling appealable rather than merely explained. The address rides on the
 * REASON rather than being fetched here, because a ruling is appealable under
 * the terms that were in force when it was made: a node that gains an appeal
 * route tomorrow has not made yesterday's rulings appealable retroactively.
 *
 * Null when no reason carries one, which is what `Reason.appeal` already means:
 * absent means no appeal path exists yet.
 */
export function appealLine(
  reasons: readonly { appeal?: string }[],
  feedId: string,
  atMs: number,
): string | null {
  const contact = reasons.find((r) => (r.appeal ?? '').trim() !== '')?.appeal?.trim();
  if (!contact) return null;
  const when = new Date(atMs).toISOString().replace('T', ' ').slice(0, 16);
  return `Disagree with this? Write to ${contact}, quoting feed ${feedId} and ${when} UTC.`;
}

/**
 * What a monitor tile says when it has no picture, and WHY it has none.
 *
 * **Issue 019b, and it is this repository's ninth-plus instrument-as-judgment
 * defect wearing a rectangle.** A WHEP read refused with 401 was drawn as a
 * black tile, which is exactly what a camera sending no pixels looks like, so
 * an operator reported the monitor as broken three times in one session and it
 * was correct every time. The credential fix removed that cause and not the
 * class: any refusal, on any path, was still drawn as a dark picture.
 *
 * **Three answers, never two**, which is the same shape as the sidecar banner
 * and `recordAudit` taking an observation rather than a boolean:
 *
 *   REFUSED       the media plane answered, and said no. Ours to fix, and the
 *                 status is the thing worth reading.
 *   UNREACHABLE   nothing answered. An outage between here and the node, and a
 *                 different problem from a refusal even though both end in no
 *                 picture.
 *   DARK          connected, negotiated, and no pixels arrived. The only one of
 *                 the three that is a claim about the CAMERA, and the one the
 *                 other two were being confused with.
 *
 * Pure and here rather than in the DOM function, because nothing executes the
 * client and a string built inside a renderer is a string no test can reach:
 * seven of the eleven defects the wave 3 reviews found lived in `src/client/`
 * and not one was found by a test.
 */
export type MonitorFault =
  | { kind: 'refused'; status: number; line: string }
  | { kind: 'unreachable'; line: string }
  | { kind: 'dark'; line: string };

export function monitorFault(err: unknown): MonitorFault {
  /*
   * `status` is read off the value rather than parsed out of the message. A
   * message is prose and a status is a number, and this repository has already
   * paid for a check that pinned a NAME and could not follow it.
   */
  const status = typeof err === 'object' && err !== null && 'status' in err
    && typeof (err as { status: unknown }).status === 'number'
    ? (err as { status: number }).status
    : null;
  if (status === null) {
    return {
      kind: 'unreachable',
      line: 'No answer from the media plane. This is the link to the node, not this camera.',
    };
  }
  /*
   * 401 and 403 get their own sentence because they are the ones an operator
   * can act on, and because the wrong reading of them is the one that cost
   * three false reports: a refused read looks like a broken camera.
   */
  const why = status === 401 || status === 403
    ? 'Refused by the media plane, so this is a permission and not a picture.'
    : `The media plane answered ${String(status)}.`;
  return { kind: 'refused', status, line: `${why} Nothing here says the camera is dark.` };
}

/** The tile has a live connection and no pixels. The one claim about the camera. */
export function darkFault(): MonitorFault {
  return {
    kind: 'dark',
    line: 'Connected, and no picture has arrived. This one IS the camera.',
  };
}
